import Phaser from 'phaser';

/**
 * PauseScene: Overlay pause menu with Resume, Volume controls (slider + mute), and Quit options.
 * Supports full keyboard navigation with arrow keys/Tab and Enter/Space selection.
 */
export class PauseScene extends Phaser.Scene {
  private canResume = false;
  private sliderKnob!: Phaser.GameObjects.Rectangle;
  private sliderTrack!: Phaser.GameObjects.Rectangle;
  private sliderFill!: Phaser.GameObjects.Rectangle;
  private volumeValueText!: Phaser.GameObjects.Text;
  private knobGlow!: Phaser.GameObjects.Rectangle;
  private sliderX = 0;
  private sliderWidth = 200;

  // Keyboard navigation
  private menuItems: { text: Phaser.GameObjects.Text; action: () => void; defaultColor: string; defaultGlowColor: string }[] = [];
  private selectedIndex = 0;
  private focusArrow!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'PauseScene' });
  }

  create(): void {
    const { width, height } = this.cameras.main;

    // Reset menu state
    this.menuItems = [];
    this.selectedIndex = 0;

    // Prevent immediate resume from the same key press that triggered pause
    this.canResume = false;
    this.time.delayedCall(200, () => {
      this.canResume = true;
    });

    // Semi-transparent background overlay
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.75);
    overlay.setDepth(0);

    // Pause title with neon glow
    this.add.text(width / 2, height * 0.2, 'PAUSED', {
      fontSize: '36px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#00ffff', blur: 15, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Resume button with glow
    const resumeBtn = this.add.text(width / 2, height * 0.38, 'RESUME', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#ffffff', blur: 8, fill: true, stroke: true,
      },
    });
    resumeBtn.setOrigin(0.5, 0.5);
    resumeBtn.setInteractive({ useHandCursor: true });
    resumeBtn.on('pointerover', () => {
      this.selectedIndex = 0;
      this.updateMenuHighlight();
    });
    resumeBtn.on('pointerout', () => this.resetItemStyle(0));
    resumeBtn.on('pointerdown', () => this.resumeGame());
    this.menuItems.push({ text: resumeBtn, action: () => this.resumeGame(), defaultColor: '#ffffff', defaultGlowColor: '#ffffff' });

    // Volume section
    const volumeY = height * 0.52;

    // Volume label with green glow
    this.add.text(width / 2, volumeY - 22, 'VOLUME', {
      fontSize: '14px',
      color: '#00ff41',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#00ff41', blur: 6, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Volume slider
    this.sliderWidth = 200;
    this.sliderX = width / 2 - this.sliderWidth / 2;
    const sliderY = volumeY + 12;

    // Slider track background (dark)
    this.sliderTrack = this.add.rectangle(
      width / 2, sliderY, this.sliderWidth, 8, 0x333333
    );
    this.sliderTrack.setDepth(1);

    // Slider fill (green glow)
    const isMuted = this.registry.get('audioMuted') ?? false;
    let currentVolume = 1;
    try {
      currentVolume = isMuted ? 0 : ((this.game.sound as Phaser.Sound.WebAudioSoundManager).masterVolumeNode?.gain?.value ?? 1);
    } catch (_e) { /* audio may not be available */ }
    const fillWidth = Math.max(1, currentVolume * this.sliderWidth);
    this.sliderFill = this.add.rectangle(
      this.sliderX + fillWidth / 2, sliderY, fillWidth, 8, 0x00ff41
    );
    this.sliderFill.setDepth(2);

    // Slider knob (interactive draggable)
    const knobX = this.sliderX + currentVolume * this.sliderWidth;
    this.sliderKnob = this.add.rectangle(knobX, sliderY, 14, 20, 0x00ffff);
    this.sliderKnob.setDepth(3);
    this.sliderKnob.setInteractive({ useHandCursor: true, draggable: true });

    // Knob glow effect
    this.knobGlow = this.add.rectangle(knobX, sliderY, 18, 24, 0x00ffff, 0.3);
    this.knobGlow.setDepth(2);

    // Volume percentage text
    const volumePercent = Math.round(currentVolume * 100);
    this.volumeValueText = this.add.text(width / 2 + this.sliderWidth / 2 + 55, sliderY, `${volumePercent}%`, {
      fontSize: '10px',
      color: '#00ff41',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#00ff41', blur: 4, fill: true, stroke: true,
      },
    });
    this.volumeValueText.setOrigin(0.5, 0.5);

    // - and + buttons for volume
    const minusBtn = this.add.text(this.sliderX - 25, sliderY, '-', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#ffffff', blur: 6, fill: true, stroke: true,
      },
    });
    minusBtn.setOrigin(0.5, 0.5);
    minusBtn.setInteractive({ useHandCursor: true });
    minusBtn.on('pointerover', () => {
      minusBtn.setColor('#00ffff');
      minusBtn.setShadow(0, 0, '#00ffff', 10, true, true);
    });
    minusBtn.on('pointerout', () => {
      minusBtn.setColor('#ffffff');
      minusBtn.setShadow(0, 0, '#ffffff', 6, true, true);
    });
    minusBtn.on('pointerdown', () => {
      const newVol = Math.max(0, this.getDisplayVolume() - 0.1);
      this.setVolume(newVol);
      this.updateSliderVisuals(newVol);
    });

    const plusBtn = this.add.text(this.sliderX + this.sliderWidth + 25, sliderY, '+', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#ffffff', blur: 6, fill: true, stroke: true,
      },
    });
    plusBtn.setOrigin(0.5, 0.5);
    plusBtn.setInteractive({ useHandCursor: true });
    plusBtn.on('pointerover', () => {
      plusBtn.setColor('#00ffff');
      plusBtn.setShadow(0, 0, '#00ffff', 10, true, true);
    });
    plusBtn.on('pointerout', () => {
      plusBtn.setColor('#ffffff');
      plusBtn.setShadow(0, 0, '#ffffff', 6, true, true);
    });
    plusBtn.on('pointerdown', () => {
      const newVol = Math.min(1, this.getDisplayVolume() + 0.1);
      this.setVolume(newVol);
      this.updateSliderVisuals(newVol);
    });

    // Make the slider track clickable to set volume
    this.sliderTrack.setInteractive({ useHandCursor: true });
    this.sliderTrack.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const newVol = Phaser.Math.Clamp((pointer.x - this.sliderX) / this.sliderWidth, 0, 1);
      this.setVolume(newVol);
      this.updateSliderVisuals(newVol);
    });

    // Dragging the knob
    this.input.on('drag', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject, dragX: number) => {
      if (gameObject === this.sliderKnob) {
        const clampedX = Phaser.Math.Clamp(dragX, this.sliderX, this.sliderX + this.sliderWidth);
        const newVol = (clampedX - this.sliderX) / this.sliderWidth;
        this.setVolume(newVol);
        this.updateSliderVisuals(newVol);
      }
    });

    // Mute toggle with glow
    const muteY = volumeY + 45;
    const muteText = (this.registry.get('audioMuted') ?? false) ? 'UNMUTE' : 'MUTE';
    const muteBtn = this.add.text(width / 2, muteY, muteText, {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#ffffff', blur: 6, fill: true, stroke: true,
      },
    });
    muteBtn.setOrigin(0.5, 0.5);
    muteBtn.setInteractive({ useHandCursor: true });
    muteBtn.on('pointerover', () => {
      this.selectedIndex = 1;
      this.updateMenuHighlight();
    });
    muteBtn.on('pointerout', () => this.resetItemStyle(1));
    muteBtn.on('pointerdown', () => this.toggleMute(muteBtn));
    this.menuItems.push({
      text: muteBtn,
      action: () => this.toggleMute(muteBtn),
      defaultColor: '#ffffff',
      defaultGlowColor: '#ffffff',
    });

    // Quit to Title button with red glow
    const quitBtn = this.add.text(width / 2, height * 0.82, 'QUIT TO TITLE', {
      fontSize: '16px',
      color: '#ff3333',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#ff3333', blur: 8, fill: true, stroke: true,
      },
    });
    quitBtn.setOrigin(0.5, 0.5);
    quitBtn.setInteractive({ useHandCursor: true });
    quitBtn.on('pointerover', () => {
      this.selectedIndex = 2;
      this.updateMenuHighlight();
    });
    quitBtn.on('pointerout', () => this.resetItemStyle(2));
    quitBtn.on('pointerdown', () => {
      this.scene.stop('GameScene');
      this.scene.start('TitleScene');
    });
    this.menuItems.push({
      text: quitBtn,
      action: () => {
        this.scene.stop('GameScene');
        this.scene.start('TitleScene');
      },
      defaultColor: '#ff3333',
      defaultGlowColor: '#ff3333',
    });

    // Focus arrow indicator (▶) showing currently selected item
    this.focusArrow = this.add.text(0, 0, '▶', {
      fontSize: '14px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#00ffff', blur: 10, fill: true, stroke: true,
      },
    });
    this.focusArrow.setOrigin(0.5, 0.5);

    // Pulsing animation for focus arrow
    this.tweens.add({
      targets: this.focusArrow,
      alpha: { from: 0.6, to: 1.0 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Help text for keyboard controls
    this.add.text(width / 2, height * 0.92, '↑↓ NAVIGATE  ENTER SELECT  ESC RESUME', {
      fontSize: '7px',
      color: '#8888aa',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#8888aa', blur: 4, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Keyboard navigation
    this.input.keyboard?.on('keydown-UP', () => this.navigateMenu(-1));
    this.input.keyboard?.on('keydown-DOWN', () => this.navigateMenu(1));
    this.input.keyboard?.on('keydown-TAB', (event: KeyboardEvent) => {
      event.preventDefault();
      this.navigateMenu(1);
    });
    this.input.keyboard?.on('keydown-ENTER', () => this.selectMenuItem());
    this.input.keyboard?.on('keydown-SPACE', () => this.selectMenuItem());

    // Left/Right arrows adjust volume when on volume/mute item
    this.input.keyboard?.on('keydown-LEFT', () => {
      if (this.selectedIndex === 1) {
        const currentVol = this.getDisplayVolume();
        const newVol = Math.max(0, currentVol - 0.1);
        this.setVolume(newVol);
        this.updateSliderVisuals(newVol);
      }
    });
    this.input.keyboard?.on('keydown-RIGHT', () => {
      if (this.selectedIndex === 1) {
        const currentVol = this.getDisplayVolume();
        const newVol = Math.min(1, currentVol + 0.1);
        this.setVolume(newVol);
        this.updateSliderVisuals(newVol);
      }
    });

    // Keyboard to resume (all pause keys should also resume)
    this.input.keyboard?.on('keydown-ESC', () => this.resumeGame());
    this.input.keyboard?.on('keydown-P', () => this.resumeGame());
    this.input.keyboard?.on('keydown-F3', (event: KeyboardEvent) => {
      event.preventDefault();
      this.resumeGame();
    });
    if (this.input.keyboard) {
      const pauseKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.PAUSE);
      pauseKey.on('down', () => this.resumeGame());
    }

    // Highlight first menu item
    this.updateMenuHighlight();
  }

  private navigateMenu(direction: number): void {
    this.selectedIndex = (this.selectedIndex + direction + this.menuItems.length) % this.menuItems.length;
    this.updateMenuHighlight();
  }

  private selectMenuItem(): void {
    if (this.menuItems[this.selectedIndex]) {
      this.menuItems[this.selectedIndex].action();
    }
  }

  private updateMenuHighlight(): void {
    for (let i = 0; i < this.menuItems.length; i++) {
      const item = this.menuItems[i];
      if (i === this.selectedIndex) {
        item.text.setColor('#00ffff');
        item.text.setShadow(0, 0, '#00ffff', 12, true, true);
      } else {
        item.text.setColor(item.defaultColor);
        item.text.setShadow(0, 0, item.defaultGlowColor, 8, true, true);
      }
    }
    // Position focus arrow to the left of the selected item
    if (this.focusArrow && this.menuItems[this.selectedIndex]) {
      const selectedText = this.menuItems[this.selectedIndex].text;
      const bounds = selectedText.getBounds();
      this.focusArrow.setPosition(bounds.x - 20, bounds.y + bounds.height / 2);
    }
  }

  private resetItemStyle(index: number): void {
    if (index !== this.selectedIndex && this.menuItems[index]) {
      const item = this.menuItems[index];
      item.text.setColor(item.defaultColor);
      item.text.setShadow(0, 0, item.defaultGlowColor, 8, true, true);
    }
  }

  private toggleMute(muteBtn: Phaser.GameObjects.Text): void {
    // Use game registry to store mute state + WebAudio gain nodes for actual audio control
    const wasMuted = this.registry.get('audioMuted') ?? false;
    const newMuted = !wasMuted;
    this.registry.set('audioMuted', newMuted);
    let displayVol = newMuted ? 0 : 1;
    // Apply mute via the WebAudio masterMuteNode gain.value (setValueAtTime is unreliable)
    const soundMgr = this.game.sound as Phaser.Sound.WebAudioSoundManager;
    if (newMuted) {
      // Save current volume before muting
      const currentVol = soundMgr.masterVolumeNode?.gain?.value ?? 1;
      this.registry.set('audioVolume', currentVol);
      if (soundMgr.masterMuteNode?.gain) { soundMgr.masterMuteNode.gain.value = 0; }
    } else {
      // Unmute
      if (soundMgr.masterMuteNode?.gain) { soundMgr.masterMuteNode.gain.value = 1; }
      displayVol = soundMgr.masterVolumeNode?.gain?.value ?? 1;
    }
    muteBtn.setText(newMuted ? 'UNMUTE' : 'MUTE');
    this.updateSliderVisuals(displayVol);
  }

  private getVolume(): number {
    try {
      // If muted, return the saved volume (so slider adjustments use the pre-mute value)
      if (this.registry.get('audioMuted') ?? false) {
        return this.registry.get('audioVolume') ?? 1;
      }
      return (this.game.sound as Phaser.Sound.WebAudioSoundManager).masterVolumeNode?.gain?.value ?? 1;
    } catch {
      return 1;
    }
  }

  /** Returns the currently displayed volume (0 when muted, actual gain value otherwise). */
  private getDisplayVolume(): number {
    try {
      if (this.registry.get('audioMuted') ?? false) {
        return 0;
      }
      return (this.game.sound as Phaser.Sound.WebAudioSoundManager).masterVolumeNode?.gain?.value ?? 1;
    } catch {
      return 1;
    }
  }

  private setVolume(vol: number): void {
    const clampedVol = Phaser.Math.Clamp(vol, 0, 1);
    // Set volume directly on the WebAudio gain node value (setValueAtTime is unreliable)
    const soundMgr = this.game.sound as Phaser.Sound.WebAudioSoundManager;
    if (soundMgr.masterVolumeNode?.gain) { soundMgr.masterVolumeNode.gain.value = clampedVol; }
    if (clampedVol > 0 && (this.registry.get('audioMuted') ?? false)) {
      this.registry.set('audioMuted', false);
      // Unmute the audio path
      if (soundMgr.masterMuteNode?.gain) { soundMgr.masterMuteNode.gain.value = 1; }
      // Update the mute button text to reflect unmuted state
      if (this.menuItems[1]) {
        this.menuItems[1].text.setText('MUTE');
      }
    }
    // Save volume to registry for persistence
    this.registry.set('audioVolume', clampedVol);
  }

  private updateSliderVisuals(vol: number): void {
    const fillWidth = Math.max(1, vol * this.sliderWidth);
    this.sliderFill.setPosition(this.sliderX + fillWidth / 2, this.sliderFill.y);
    this.sliderFill.setSize(fillWidth, 8);

    const knobX = this.sliderX + vol * this.sliderWidth;
    this.sliderKnob.setPosition(knobX, this.sliderKnob.y);
    this.knobGlow.setPosition(knobX, this.knobGlow.y);

    const percent = Math.round(vol * 100);
    this.volumeValueText.setText(`${percent}%`);
  }

  private resumeGame(): void {
    if (!this.canResume) return;
    this.scene.resume('GameScene');
    this.scene.stop();
  }

  /**
   * Clean up keyboard listeners when scene is shut down.
   * Prevents duplicate listeners from accumulating across pause/resume cycles.
   */
  shutdown(): void {
    this.input.keyboard?.removeAllListeners();
    this.input.removeAllListeners();
    // Kill all tweens to prevent leaked references (e.g., focus arrow pulse)
    this.tweens.killAll();
    // Remove all timer events (e.g., canResume delay)
    this.time.removeAllEvents();
    // Clear menu items array to release text object references
    this.menuItems = [];
  }
}
